/**
 * Nombres de fichero libres dentro de una carpeta compartida — lógica PURA.
 *
 * Vivían en `OpenCameraModal/capture.ts` (la ráfaga de la cámara); se sacaron
 * aquí para que los use también el croquis de los asistentes
 * (`utils/sketchFile.ts`) sin arrastrar la cámara ni la compresión de fotos.
 * `capture.ts` los reexporta.
 */

/**
 * Nombre libre dentro de un conjunto ya usado.
 *
 * 🔴 Por qué existe: `generateFileName` sella la hora con resolución de SEGUNDO
 * (`dd_MM_yy-HH_mm_ss`). Dos fotos del mismo segundo salen con el MISMO nombre,
 * y ese nombre es el `id` de la imagen y la clave con la que el fichero se
 * guarda en OPFS → la segunda pisa a la primera y se pierde una foto.
 *
 * Hasta ahora esto era inalcanzable por accidente: había que reabrir el modal
 * para cada foto y eso tarda más de un segundo. La ráfaga quita esa protección
 * accidental, así que la unicidad pasa a ser explícita.
 */
export const makeUniqueName = (
  base: string,
  taken: ReadonlySet<string>
): string => {
  if (!taken.has(base)) return base;
  let n = 2;
  while (taken.has(`${base}_${n}`)) n += 1;
  return `${base}_${n}`;
};

/**
 * ¿Entró ya una foto que se eligió con el nombre `fileName` y se RENOMBRÓ al
 * entrar (`makeUniqueFileName`)? Cada entrada renombrada guarda el nombre
 * original (`originalName`): sin él, volver a elegir la misma `foto.jpg` no
 * casaba con `foto_2.jpg` y entraba otra vez como `foto_3.jpg` (Ruling 19).
 */
export const wasPickedAs = (
  entries: ReadonlyArray<{ originalName?: string }>,
  fileName: string
): boolean => entries.some((entry) => entry.originalName === fileName);

/**
 * Como `makeUniqueName`, pero para un nombre de FICHERO: el sufijo va delante
 * de la extensión (`foto.jpg` → `foto_2.jpg`) para que siga siendo una imagen.
 *
 * Lo usa la foto de accidente elegida de la galería cuando su nombre ya está
 * en la carpeta `accident` de OPFS, que comparten las pestañas y los
 * asistentes de crear y editar accidente: entra con un nombre libre en vez de
 * pisar o borrar el fichero de otro borrador (spec 2026-11-02
 * wizard-draft-ownership, revisión de la Task 7).
 */
export const makeUniqueFileName = (
  name: string,
  taken: ReadonlySet<string>
): string => {
  if (!taken.has(name)) return name;
  const dot = name.lastIndexOf('.');
  if (dot <= 0) return makeUniqueName(name, taken);
  const extension = name.slice(dot);
  const stems = new Set(
    [...taken]
      .filter((takenName) => takenName.endsWith(extension))
      .map((takenName) => takenName.slice(0, -extension.length))
  );
  return `${makeUniqueName(name.slice(0, dot), stems)}${extension}`;
};
